import React, { useState } from 'react';
import { useCustomers } from '../hooks/useCustomers';
import { useRoleAccess } from '../../users/hooks/useRoleAccess';
import { PhoneUtils } from '@cleanpro/common-ui';

// Reusable form field component
interface FormFieldProps {
  id: string;
  label: string;
  type?: 'text' | 'email' | 'tel' | 'textarea';
  value: string;
  error?: string;
  placeholder?: string;
  rows?: number;
  onChange: (value: string) => void;
}

function FormField({ id, label, type = 'text', value, error, placeholder, rows, onChange }: FormFieldProps) {
  const baseClassName = `mt-1 block w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-1 ${
    error 
      ? 'border-red-300 focus:ring-red-500 focus:border-red-500' 
      : 'border-gray-300 focus:ring-indigo-500 focus:border-indigo-500'
  }`;

  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-gray-700">
        {label}
      </label>
      {type === 'textarea' ? (
        <textarea
          id={id}
          className={baseClassName}
          rows={rows || 2}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      ) : (
        <input
          id={id}
          type={type}
          className={baseClassName}
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
      {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
    </div>
  );
}

export default function CreateCustomer() {
  const { createCustomer } = useCustomers();
  const permissions = useRoleAccess();

  const [newCustomer, setNewCustomer] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    preferences: '',
    lastService: new Date().toISOString().split('T')[0]
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const handleInputChange = (field: string, value: string) => {
    // Apply phone formatting for phone field
    if (field === 'phone') {
      value = PhoneUtils.formatIncomplete(value);
    }
    
    setNewCustomer(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleAddCustomer = (e: React.FormEvent) => {
    e.preventDefault();
    // Validate form
    const errors: Record<string, string> = {};
    if (!newCustomer.name.trim()) {
      errors.name = 'Name is required';
    }
    if (!newCustomer.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newCustomer.email)) {
      errors.email = 'Please enter a valid email address';
    }
    const phoneError = PhoneUtils.getValidationError(newCustomer.phone);
    if (phoneError) {
      errors.phone = phoneError;
    }
    if (!newCustomer.address.trim()) {
      errors.address = 'Address is required';
    }
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) {
      return;
    }
    (async () => {
      try {
        await createCustomer({
          name: newCustomer.name,
          email: newCustomer.email,
          phone: newCustomer.phone,
          address: newCustomer.address,
          preferences: newCustomer.preferences
        });
        setNewCustomer({
          name: '',
          email: '',
          phone: '',
          address: '',
          preferences: '',
          lastService: new Date().toISOString().split('T')[0]
        });
        setFormErrors({});
      } catch (_err) {
        setFormErrors({ submit: 'Failed to add customer. Please try again.' });
      }
    })();
  };

  return (
    <div className="space-y-6">
    {permissions.canEditCustomers && (
      <>
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-900">Add New Customer</h1>
      </div>

        <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-screen overflow-y-auto">
            <form onSubmit={handleAddCustomer} className="space-y-4" noValidate>
              <FormField
                id="customer-name"
                label="Name"
                value={newCustomer.name}
                error={formErrors.name}
                onChange={(value) => handleInputChange('name', value)}
              />
              
              <FormField
                id="customer-email"
                label="Email"
                type="email"
                value={newCustomer.email}
                error={formErrors.email}
                onChange={(value) => handleInputChange('email', value)}
              />
              
              <FormField
                id="customer-phone"
                label="Phone"
                type="tel"
                placeholder="(555) 123-4567"
                value={newCustomer.phone}
                error={formErrors.phone}
                onChange={(value) => handleInputChange('phone', value)}
              />
              
              <FormField
                id="customer-address"
                label="Address"
                type="textarea"
                rows={2}
                value={newCustomer.address}
                error={formErrors.address}
                onChange={(value) => handleInputChange('address', value)}
              />
              
              <FormField
                id="customer-preferences"
                label="Preferences"
                type="textarea"
                rows={2}
                value={newCustomer.preferences}
                onChange={(value) => handleInputChange('preferences', value)}
              />

              {formErrors.submit && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                  {formErrors.submit}
                </div>
              )}
              
              <div className="flex space-x-3 pt-4">
                <button
                  type="submit"
                  className="flex-1 bg-indigo-600 text-white py-2 px-4 rounded-lg hover:bg-indigo-700 transition-colors"
                >
                  Add Customer
                </button>
              </div>
            </form>
        </div>
        </>
     )}
      </div>
    );
  }